import PageSEO from "@/components/PageSEO";
import StickyHeader from "@/components/StickyHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTAFooter from "@/components/CTAFooter";
import { Link } from "react-router-dom";
import { Wrench, ThermometerSun, PlusCircle, RefreshCw, Leaf, Droplets, Smartphone, Shield, Wind, Home, Building2, GlassWater } from "lucide-react";

const services = [
  {
    title: "Air Conditioning Maintenance",
    description: "Regular maintenance to keep your system running efficiently, extend its lifespan, and ensure optimal air quality in your home or office.",
    icon: Wrench,
    image: "https://airconditioningguys.com.au/uploads/ac_services/service_img1.png",
    link: "/services/air-conditioning-maintenance",
  },
  {
    title: "Air Conditioning Repairs",
    description: "Fast, reliable repairs for all air conditioning brands and models. Our licensed technicians diagnose and fix issues promptly.",
    icon: ThermometerSun,
    image: "https://airconditioningguys.com.au/uploads/ac_services/service_img2.png",
    link: "/services/air-conditioning-repairs",
  },
  {
    title: "Air Conditioning Installation",
    description: "Professional installation of split systems, ducted systems, and multi-head units. We ensure the right system for your space.",
    icon: PlusCircle,
    image: "https://airconditioningguys.com.au/uploads/ac_services/service_img3.png",
    link: "/services/air-conditioning-installation",
  },
  {
    title: "Air Conditioning Replacements",
    description: "Upgrade your old, inefficient system with the latest energy-efficient air conditioning technology for better comfort and savings.",
    icon: RefreshCw,
    image: "https://airconditioningguys.com.au/uploads/ac_services/service_img4.png",
    link: "/services/air-conditioning-replacements",
  },
  {
    title: "Rejuvenation Eco Clean",
    description: "Deep cleaning service that removes mould, bacteria and allergens from your air conditioning system for healthier indoor air.",
    icon: Leaf,
    image: "https://airconditioningguys.com.au/uploads/ac_services/service_img5.png",
    link: "/services/rejuvenation-eco-clean",
  },
  {
    title: "Leak Repairs",
    description: "Expert detection and repair of refrigerant leaks and water leaks to restore your system's performance and prevent damage.",
    icon: Droplets,
    image: "https://airconditioningguys.com.au/uploads/ac_services/service_img6.png",
    link: "/services/leak-repairs",
  },
  {
    title: "Home Automation",
    description: "Smart home integration for your air conditioning — control your comfort from anywhere with Wi-Fi enabled systems.",
    icon: Smartphone,
    image: "https://airconditioningguys.com.au/uploads/ac_services/service_img7_001.png",
    link: "/services/home-automation",
  },
  {
    title: "Corrosion Protection",
    description: "Protect your outdoor unit from coastal and harsh environments with professional anti-corrosion coatings and treatments.",
    icon: Shield,
    image: "https://airconditioningguys.com.au/uploads/ac_services/service_img8.png",
    link: "/services/corrosion-protection",
  },
  {
    title: "Ductwork Replacement & Repairs",
    description: "Repair or replace damaged, leaking or inefficient ductwork to improve airflow, comfort and energy efficiency throughout your home.",
    icon: Wind,
    image: "https://airconditioningguys.com.au/uploads/ac_services/service_img9.png",
    link: "/services/ductwork-replacement-repairs",
  },
  {
    title: "Residential",
    description: "Complete residential air conditioning solutions — from single room split systems to whole-home ducted installations.",
    icon: Home,
    image: "https://airconditioningguys.com.au/uploads/ac_services/istock-1227524015-1.png",
    link: "/services/residential-air-conditioning",
  },
  {
    title: "Commercial",
    description: "Tailored commercial HVAC solutions for offices, retail, restaurants, and industrial spaces of all sizes.",
    icon: Building2,
    image: "https://airconditioningguys.com.au/uploads/ac_services/istock-1283757853-1.png",
    link: "/services/commercial",
  },
  {
    title: "Water Cooler",
    description: "Supply and installation of water cooler systems — keep your team refreshed with clean, filtered drinking water.",
    icon: GlassWater,
    image: "https://airconditioningguys.com.au/watercooler/assets/images/lib/watercooler.jpg",
    link: "/services/watercooler",
  },
];

const specialOffers = [
  {
    title: "Upgrade Your Air Conditioner With Wifi",
    image: "https://airconditioningguys.com.au/uploads/common_images/service_spcl_offer1.jpg",
  },
  {
    title: "Supply & Install of New Air Filter + Air Sanitiser",
    image: "https://airconditioningguys.com.au/uploads/common_images/service_spcl_offer2.jpg",
  },
];

const serviceDetails = [
  {
    title: "Air Conditioning Maintenance",
    link: "/services/air-conditioning-maintenance",
    body: "In a climate that swings from frosty winter mornings to mild summer days, your system works hard almost year-round. Regular maintenance — filter cleaning, coil checks, refrigerant pressure testing and electrical inspection — catches small issues like a struggling compressor or a partially blocked outdoor coil before they turn into a mid-winter breakdown. We recommend a pre-winter service every autumn for Highlands homes, since a system heating a Bowral or Moss Vale home through July needs to be running at full efficiency, not limping along on a dirty filter.",
  },
  {
    title: "Air Conditioning Repairs",
    link: "/services/air-conditioning-repairs",
    body: "When a system stops heating on a sub-zero morning or won't cool on a warm afternoon, our licensed technicians diagnose the fault — whether it's a failed capacitor, a refrigerant leak, a blocked drain or a control board issue — and repair it on all major brands. We carry common parts on our vehicles to fix most issues on the first visit, minimising the time your household or business goes without comfort.",
  },
  {
    title: "Air Conditioning Installation",
    link: "/services/air-conditioning-installation",
    body: "Correct installation is the single biggest factor in how well a system performs through a Highlands winter. We assess room size, insulation, ceiling access and orientation to size the system for our climate's heating demand, then install split, multi-head, ducted, cassette or bulkhead systems to manufacturer specification — proper refrigerant pipe sizing, correct condensate falls and secure outdoor unit mounting all matter for long-term reliability in frost and wind.",
  },
  {
    title: "Air Conditioning Replacements",
    link: "/services/air-conditioning-replacements",
    body: "Older systems — particularly non-inverter units over ten years old — lose heating efficiency and can struggle badly once outdoor temperatures drop below a few degrees. If your existing system is icing up excessively, running constantly without reaching temperature, or costing more each winter to run, a replacement with a modern low-ambient inverter unit often pays for itself in comfort and running costs. We handle removal of the old unit, safe refrigerant recovery and full reinstallation.",
  },
  {
    title: "Rejuvenation Eco Clean",
    link: "/services/rejuvenation-eco-clean",
    body: "Over time, indoor coils and blower wheels accumulate dust, mould and bacteria that reduce airflow and air quality. Our eco clean service strips down the indoor unit and deep-cleans the coil, fan and filters using an environmentally responsible process, restoring both airflow and healthier air — particularly valuable for households with allergies or respiratory sensitivities.",
  },
  {
    title: "Leak Repairs",
    link: "/services/leak-repairs",
    body: "A refrigerant leak reduces heating and cooling capacity and, left unchecked, can damage the compressor. We use electronic leak detection to pinpoint the source — often a joint, valve or corroded section of pipe — repair it, and re-pressure test before recharging to the correct refrigerant level. We also handle water leak issues from blocked or poorly pitched condensate drains, a common problem in ducted systems.",
  },
  {
    title: "Home Automation",
    link: "/services/home-automation",
    body: "Wi-Fi enabled controllers let you schedule heating to come on before you wake on a frosty morning, or check that you turned the system off before leaving for a weekend in Sydney. We integrate smart controllers and zone systems with most major brands, and can tie your air conditioning into a broader smart home setup.",
  },
  {
    title: "Corrosion Protection",
    link: "/services/corrosion-protection",
    body: "Outdoor units in exposed rural or elevated Highlands properties face wind-driven moisture, frost and, on some properties, agricultural chemical exposure — all of which accelerate corrosion of the outdoor coil and cabinet. We apply protective coatings and select corrosion-resistant components where appropriate, extending the working life of your outdoor unit.",
  },
  {
    title: "Ductwork Replacement & Repairs",
    link: "/services/ductwork-replacement-repairs",
    body: "Leaking, crushed or poorly insulated ductwork can quietly waste a large share of your heating output into the roof space before it ever reaches a room — a serious issue in a climate where that heat is expensive to generate. We inspect existing duct runs for leaks, insulation degradation and airflow restrictions, then repair or replace sections as needed to restore even heating and cooling to every room.",
  },
  {
    title: "Residential Air Conditioning",
    link: "/services/residential-air-conditioning",
    body: "From a single split system in a Bundanoon cottage to a fully zoned ducted system in a new Robertson build, we handle the full range of residential air conditioning — supply, installation, maintenance and repair — matched to the specific heating and cooling demands of a cool-climate home.",
  },
  {
    title: "Commercial",
    link: "/services/commercial",
    body: "Offices, retail spaces, restaurants and light industrial sites across Bowral, Mittagong and Goulburn each have different occupancy patterns, equipment heat loads and hours of operation. We design and install commercial systems — including VRV/VRF and packaged units — sized to those specific loads, and offer scheduled commercial maintenance programs to minimise downtime.",
  },
  {
    title: "Water Cooler",
    link: "/services/watercooler",
    body: "We also supply and install filtered water cooler systems for offices, workshops and hospitality venues, giving staff and customers clean, chilled drinking water without the ongoing cost of bottled water delivery.",
  },
];

const repairVsReplace = [
  { sign: "System is over 10-12 years old", verdict: "Lean towards replacement" },
  { sign: "Struggles to heat once outdoor temp drops below 2-3°C", verdict: "Replacement likely — check low-ambient rating" },
  { sign: "One-off fault: won't turn on, tripping a breaker, strange noise", verdict: "Repair — usually a single component" },
  { sign: "Repeated refrigerant leaks or a major leak in the compressor coil", verdict: "Replacement often more cost-effective" },
  { sign: "Icing up excessively on the outdoor unit each winter", verdict: "Repair if a recent fault, replace if a chronic pattern" },
  { sign: "Rising power bills for the same use pattern", verdict: "Replacement — efficiency has likely dropped" },
  { sign: "Parts are obsolete or hard to source for the model", verdict: "Replacement" },
];

const faqs = [
  {
    q: "How often should I service my air conditioner in the Southern Highlands?",
    a: "We recommend a full service once a year, ideally in autumn before winter heating season begins. Systems that run heavily through both winter and summer, or units in dusty rural settings, benefit from a second check mid-year.",
  },
  {
    q: "What actually happens during a service visit?",
    a: "Our technician cleans or replaces filters, checks and cleans the indoor coil and blower wheel, inspects the outdoor unit and coil for debris or corrosion, tests refrigerant pressures and electrical connections, checks the condensate drain for blockages, and confirms the defrost cycle and thermostat controls are functioning correctly. You'll get a written summary of what was found and any recommendations.",
  },
  {
    q: "Why is pre-winter maintenance so important here specifically?",
    a: "Our winters bring consistent sub-zero mornings, and a system heating your home for months needs to be running at full efficiency from day one. A pre-winter check catches a struggling compressor, low refrigerant or a dirty filter before it turns into no heating at all on the coldest night of the year.",
  },
  {
    q: "How do I know if I need a repair or a full replacement?",
    a: "A one-off fault on a system under about ten years old is usually worth repairing. If the system is older, has had repeated refrigerant leaks, struggles to heat once it's genuinely cold outside, or your power bills have crept up for the same usage, replacement with a modern low-ambient inverter unit is usually the better long-term investment. We'll always give you an honest recommendation and the cost of both options.",
  },
  {
    q: "What's the difference between residential and commercial servicing?",
    a: "Commercial systems are often larger, run longer hours, and may include multiple zones or VRV/VRF configurations serving several tenancies. Commercial maintenance programs are typically scheduled more frequently and prioritise minimising downtime, since a fault can affect staff, customers or stock rather than just household comfort.",
  },
  {
    q: "Do you service all brands, or only certain ones?",
    a: "We service, repair and maintain all major air conditioning brands, regardless of who originally installed the system. See our full brand range for what we supply and install new.",
  },
  {
    q: "Can outdoor units be damaged by frost?",
    a: "Frost itself doesn't damage a well-installed outdoor unit — reverse-cycle systems are designed to defrost automatically. Problems arise when a unit is undersized, poorly maintained or an older model without efficient low-ambient defrost logic, which can lead to excessive icing, reduced heating output and extra wear on the compressor over a Highlands winter.",
  },
  {
    q: "How quickly can you attend a breakdown?",
    a: "We prioritise no-heat and no-cool calls, particularly during extreme weather, and aim to attend as quickly as possible across Mittagong, Bowral, Moss Vale, Berrima, Bundanoon and surrounding Southern Highlands towns. Call 0480 841 485 or book online.",
  },
];

const Services = () => {
  return (
    <div className="min-h-screen bg-background">
      <PageSEO title="AC Services | Install, Repairs & Maintenance | ACG Southern Highlands" description="Full range of air conditioning services in the Southern Highlands — installation, repairs, maintenance, eco cleaning, ductwork and more. Licensed technicians, all brands." />
      <StickyHeader />
      <Breadcrumbs items={[{ label: "Our Services" }]} />

      {/* Hero Banner */}
      <section className="relative bg-acg-charcoal overflow-hidden">
        <img
          src="https://airconditioningguys.com.au/uploads/common_images/service_banner.jpg"
          alt="ACG technician performing professional air conditioning service and maintenance"
          className="w-full h-64 md:h-80 object-cover opacity-60"
          width={1920}
          height={320}
          loading="eager"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-primary-foreground">
            <h1 className="text-4xl md:text-5xl font-extrabold font-montserrat mb-3">
              ACG Air Conditioning Services
            </h1>
            <p className="text-lg md:text-xl font-semibold text-primary-foreground/80">
              Supply, Installations, Maintenance & Repairs
            </p>
            <p className="mt-2 text-sm text-primary-foreground/60">ARC RTA AU38134</p>
          </div>
        </div>
      </section>

      {/* Special Offers */}
      <section className="py-12 bg-muted">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8 font-montserrat text-foreground">
            Special Offers
          </h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {specialOffers.map((offer) => (
              <a
                key={offer.title}
                href="/specials"
                className="group rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow bg-card"
              >
                <img
                  src={offer.image}
                  alt={`${offer.title} — ACG special offer`}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  width={600}
                  height={192}
                  loading="lazy"
                />
                <div className="p-4">
                  <h3 className="font-bold text-foreground group-hover:text-primary transition-colors">
                    {offer.title}
                  </h3>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4 font-montserrat text-foreground">
            Our Services
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            From installations to repairs, ACG delivers quality air conditioning solutions for homes and businesses across the Southern Highlands.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <a
                  key={service.title}
                  href={service.link}
                  className="group bg-card rounded-lg border border-border overflow-hidden hover:shadow-lg hover:border-primary/30 transition-all duration-300"
                >
                  <div className="w-full h-48 bg-muted flex items-center justify-center overflow-hidden">
                    <img
                      src={service.image}
                      alt={`${service.title} — professional AC service by ACG Southern Highlands`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      width={400}
                      height={192}
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors font-montserrat">
                      {service.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed service descriptions */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4 max-w-4xl space-y-10">
          <h2 className="text-3xl font-bold font-montserrat text-foreground">
            Our Services in Detail
          </h2>
          {serviceDetails.map((s) => (
            <div key={s.title}>
              <h3 className="text-2xl font-bold mb-2 text-foreground font-montserrat">
                <Link to={s.link} className="hover:text-primary transition-colors">{s.title}</Link>
              </h3>
              <p className="text-muted-foreground leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What happens during a service */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-6 font-montserrat text-foreground">
            What Happens During a Service Visit
          </h2>
          <ol className="space-y-4 text-muted-foreground leading-relaxed list-decimal pl-5">
            <li><strong className="text-foreground">Initial check and history.</strong> We ask about any issues you've noticed — reduced heating, unusual noise, higher bills — and check the system's service history.</li>
            <li><strong className="text-foreground">Filter and indoor unit clean.</strong> Filters are cleaned or replaced, and the indoor coil and blower wheel are inspected and cleaned if needed to keep airflow and air quality up.</li>
            <li><strong className="text-foreground">Outdoor unit inspection.</strong> We clear leaves, debris and grass clippings from the outdoor coil, check the fan motor, and look for early corrosion — especially important on exposed rural properties.</li>
            <li><strong className="text-foreground">Refrigerant and electrical testing.</strong> Pressures are checked against manufacturer specification, and electrical connections, capacitors and controls are tested for wear.</li>
            <li><strong className="text-foreground">Defrost and drain check.</strong> We confirm the defrost cycle triggers and clears properly — critical for winter performance — and check the condensate drain isn't blocked.</li>
            <li><strong className="text-foreground">Written report.</strong> You receive a summary of what was checked, any parts nearing end of life, and clear recommendations with no pressure to act immediately.</li>
          </ol>
        </div>
      </section>

      {/* Seasonal maintenance timing */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-6 font-montserrat text-foreground">
            Seasonal Maintenance Timing for a Cool Climate
          </h2>
          <p className="text-muted-foreground mb-4 leading-relaxed">
            Because the Southern Highlands relies on air conditioning primarily for heating through a long, cold season, our maintenance recommendations are built around that pattern rather than a generic cooling-season schedule.
          </p>
          <ul className="space-y-4 text-muted-foreground leading-relaxed list-disc pl-5">
            <li><strong className="text-foreground">Pre-winter check (March-May):</strong> The most important service of the year. We confirm heating capacity, defrost cycle performance and refrigerant levels before the first frosts arrive in Mittagong, Bowral and Moss Vale.</li>
            <li><strong className="text-foreground">Filter and coil cleaning:</strong> Ideally every 3-6 months for households running heating heavily, since a clogged filter or dusty coil reduces heating output and increases running costs exactly when you need efficiency most.</li>
            <li><strong className="text-foreground">Outdoor unit and frost checks (mid-winter):</strong> A mid-season check on properties with heavy frost exposure or older units helps catch icing issues, worn fan motors or debris build-up before they cause a breakdown on the coldest night of the year.</li>
            <li><strong className="text-foreground">Pre-summer check (September-November):</strong> A shorter check to confirm cooling performance and clean the system after a winter of heavy use, ahead of our milder but still warm summer months.</li>
          </ul>
        </div>
      </section>

      {/* Repair vs replace */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold mb-6 font-montserrat text-foreground">
            Repair or Replace? Signs to Watch For
          </h2>
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full text-sm text-left">
              <thead className="bg-acg-charcoal text-primary-foreground">
                <tr>
                  <th className="p-3 font-semibold">Sign</th>
                  <th className="p-3 font-semibold">Likely Verdict</th>
                </tr>
              </thead>
              <tbody>
                {repairVsReplace.map((row, i) => (
                  <tr key={row.sign} className={i % 2 === 0 ? "bg-card" : "bg-muted/50"}>
                    <td className="p-3 text-muted-foreground align-top">{row.sign}</td>
                    <td className="p-3 font-semibold text-foreground align-top">{row.verdict}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            Not sure which applies to your system? Book a <Link to="/schedule-service" className="text-primary font-semibold hover:underline">service visit</Link> and we'll give you an honest assessment along with clear pricing for repair versus replacement — see our <Link to="/services/air-conditioning-replacements" className="text-primary hover:underline">replacement service</Link> for details.
          </p>
        </div>
      </section>

      {/* Commercial vs residential */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-6 font-montserrat text-foreground">
            Commercial vs Residential Air Conditioning
          </h2>
          <p className="text-muted-foreground mb-4 leading-relaxed">
            <Link to="/services/residential-air-conditioning" className="text-primary hover:underline">Residential</Link> systems are generally sized around the heating and cooling needs of a household, with maintenance timed around when the family is home and uses the system most heavily — typically overnight and evenings through winter.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            <Link to="/services/commercial" className="text-primary hover:underline">Commercial</Link> systems in Bowral, Mittagong and Goulburn offices, retail spaces and hospitality venues often run longer hours, serve larger or multi-zone spaces, and may use VRV/VRF technology to manage simultaneous heating and cooling demands across a building. Commercial maintenance is usually scheduled more frequently, since downtime affects staff and customers rather than just household comfort, and often needs to be timed outside trading hours.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-8 font-montserrat text-foreground">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.q} className="bg-card rounded-lg border border-border p-6">
                <h3 className="text-lg font-bold text-foreground mb-2 font-montserrat">{faq.q}</h3>
                <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-muted-foreground">
            Ready to book, or want advice on the right service for your system? See our{" "}
            <Link to="/ac-types" className="text-primary font-semibold hover:underline">AC types guide</Link>,{" "}
            <Link to="/brands" className="text-primary font-semibold hover:underline">brands</Link>, or{" "}
            <Link to="/contact-us" className="text-primary font-semibold hover:underline">contact ACG Southern Highlands</Link> on 0480 841 485.
          </p>
        </div>
      </section>

      <CTAFooter />
    </div>
  );
};

export default Services;
